import { Injectable } from '@angular/core';
import { readStoredAuthToken } from './auth.service';

type MessageHandler = (payload: unknown) => void;

interface SubscriptionEntry {
  destination: string;
  handler: MessageHandler;
}

type RealtimeProtocolMode = 'stomp' | 'raw';

@Injectable({ providedIn: 'root' })
export class RealtimeStompService {
  private readonly debugEnabled = this.resolveDebugEnabled();
  private ws: WebSocket | null = null;
  private wsBaseUrl = '';
  private connected = false;
  private mode: RealtimeProtocolMode = 'stomp';
  private connectHandshakeTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private frameBuffer = '';
  private sequence = 0;
  private subscriptions = new Map<string, SubscriptionEntry>();
  private destinationToId = new Map<string, string>();

  connect(wsUrl: string): void {
    this.wsBaseUrl = wsUrl;
    this.debug('connect() called', { wsUrl: this.redactToken(wsUrl) });
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      this.debug('connect() ignored because socket is already active', { readyState: this.ws.readyState });
      return;
    }
    this.openSocket();
  }

  subscribe(destination: string, handler: MessageHandler): () => void {
    const existingId = this.destinationToId.get(destination);
    if (existingId) {
      this.subscriptions.set(existingId, { destination, handler });
      this.debug('updated existing subscription handler', { id: existingId, destination });
      return () => this.unsubscribeById(existingId);
    }

    const id = `sub-${++this.sequence}`;
    this.destinationToId.set(destination, id);
    this.subscriptions.set(id, { destination, handler });
    this.debug('registered subscription', { id, destination, connected: this.connected, mode: this.mode });
    if (this.connected) {
      if (this.mode === 'stomp') {
        this.sendStompSubscribe(id, destination);
      } else {
        this.sendRawSubscribe(destination);
      }
    }
    return () => this.unsubscribeById(id);
  }

  private unsubscribeById(id: string): void {
    const entry = this.subscriptions.get(id);
    if (!entry) return;
    if (this.connected) {
      if (this.mode === 'stomp') {
        this.sendFrame('UNSUBSCRIBE', { id });
      } else {
        this.sendRawUnsubscribe(entry.destination);
      }
    }
    this.destinationToId.delete(entry.destination);
    this.subscriptions.delete(id);
    this.debug('unsubscribed', { id, destination: entry.destination });
  }

  private openSocket(): void {
    const wsUrl = this.withAuthToken(this.wsBaseUrl);
    this.debug('opening websocket', { wsUrl: this.redactToken(wsUrl) });
    try {
      this.ws = new WebSocket(wsUrl);
    } catch (error) {
      console.error('Failed to initialize websocket', error);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.connected = false;
      this.mode = 'stomp';
      const token = readStoredAuthToken();
      this.debug('socket open; sending STOMP CONNECT', { hasToken: !!token, subscriptions: this.subscriptions.size });
      this.sendFrame('CONNECT', {
        'accept-version': '1.2,1.1',
        'heart-beat': '10000,10000',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      });
      this.connectHandshakeTimer = setTimeout(() => {
        // API Gateway websocket integrations commonly use raw JSON instead of STOMP.
        if (this.connected) return;
        this.mode = 'raw';
        this.connected = true;
        this.debug('STOMP CONNECT timeout; falling back to raw mode');
        this.flushSubscriptions();
      }, 1200);
    };

    this.ws.onmessage = (event: MessageEvent<string>) => {
      this.frameBuffer += event.data || '';
      this.debug('socket message received', {
        mode: this.mode,
        length: (event.data || '').length,
      });
      if (this.frameBuffer.includes('\u0000')) {
        this.consumeFrames();
      } else {
        const payload = (event.data || '').trim();
        if (!payload) return;
        this.handleRawPayload(payload);
      }
    };

    this.ws.onclose = (event: CloseEvent) => {
      this.connected = false;
      this.ws = null;
      this.mode = 'stomp';
      this.clearConnectHandshakeTimer();
      console.warn('WebSocket closed', {
        code: event.code,
        reason: event.reason || 'no reason',
        wasClean: event.wasClean,
        url: wsUrl,
      });
      this.debug('socket closed; scheduling reconnect', { reconnectInMs: 2000 });
      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      this.connected = false;
      this.clearConnectHandshakeTimer();
      console.warn('WebSocket error', { url: wsUrl });
      this.debug('socket error event fired');
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || !this.wsBaseUrl) return;
    this.debug('scheduling reconnect', { wsBaseUrl: this.redactToken(this.wsBaseUrl) });
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.debug('reconnect timer fired');
      this.openSocket();
    }, 2000);
  }

  private clearConnectHandshakeTimer(): void {
    if (!this.connectHandshakeTimer) return;
    clearTimeout(this.connectHandshakeTimer);
    this.connectHandshakeTimer = null;
  }

  private withAuthToken(rawUrl: string): string {
    if (!rawUrl) return rawUrl;
    const token = readStoredAuthToken();
    if (!token) return rawUrl;

    try {
      const url = new URL(rawUrl, window.location.origin);
      url.searchParams.set('token', token);
      this.debug('attached token as query param', { url: this.redactToken(url.toString()) });
      return url.toString();
    } catch {
      const separator = rawUrl.includes('?') ? '&' : '?';
      const next = `${rawUrl}${separator}token=${encodeURIComponent(token)}`;
      this.debug('attached token using string fallback', { url: this.redactToken(next) });
      return next;
    }
  }

  private consumeFrames(): void {
    while (true) {
      const end = this.frameBuffer.indexOf('\u0000');
      if (end === -1) break;
      const rawFrame = this.frameBuffer.slice(0, end);
      this.frameBuffer = this.frameBuffer.slice(end + 1);
      const frame = rawFrame.trim();
      if (!frame) continue;
      this.handleFrame(frame);
    }
  }

  private handleFrame(frameText: string): void {
    const lines = frameText.split('\n');
    const command = lines[0]?.trim();
    if (!command) return;

    const headers: Record<string, string> = {};
    let i = 1;
    for (; i < lines.length; i++) {
      const line = lines[i];
      if (line === '') {
        i++;
        break;
      }
      const idx = line.indexOf(':');
      if (idx > -1) headers[line.slice(0, idx)] = line.slice(idx + 1);
    }

    const body = lines.slice(i).join('\n');
    this.debug('parsed STOMP frame', {
      command,
      headers: Object.keys(headers),
      bodyPreview: this.preview(body),
    });

    if (command === 'CONNECTED') {
      this.clearConnectHandshakeTimer();
      this.mode = 'stomp';
      this.connected = true;
      this.debug('received STOMP CONNECTED frame');
      this.flushSubscriptions();
      return;
    }

    if (command === 'MESSAGE') {
      const subId = headers['subscription'];
      if (!subId) {
        this.debug('dropping STOMP MESSAGE without subscription header', { headers });
        return;
      }
      const sub = this.subscriptions.get(subId);
      if (!sub) {
        this.debug('dropping STOMP MESSAGE for unknown subscription', { subId });
        return;
      }
      try {
        const payload = JSON.parse(body);
        this.debug('dispatching STOMP MESSAGE payload', { subId, destination: sub.destination });
        sub.handler(payload);
      } catch {
        this.debug('dispatching STOMP MESSAGE raw body', { subId, destination: sub.destination });
        sub.handler(body);
      }
    }
  }

  private flushSubscriptions(): void {
    this.debug('flushing subscriptions', { count: this.subscriptions.size, mode: this.mode });
    for (const [id, entry] of this.subscriptions.entries()) {
      if (this.mode === 'stomp') {
        this.sendStompSubscribe(id, entry.destination);
      } else {
        this.sendRawSubscribe(entry.destination);
      }
    }
  }

  private sendStompSubscribe(id: string, destination: string): void {
    this.debug('sending STOMP SUBSCRIBE', { id, destination });
    this.sendFrame('SUBSCRIBE', {
      id,
      destination,
    });
  }

  private sendRawSubscribe(topic: string): void {
    this.debug('sending raw subscribe', { topic });
    this.sendJson({
      action: 'subscribe',
      topic,
    });
  }

  private sendRawUnsubscribe(topic: string): void {
    this.debug('sending raw unsubscribe', { topic });
    this.sendJson({
      action: 'unsubscribe',
      topic,
    });
  }

  private sendJson(payload: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.debug('sending raw JSON payload', { payload });
    this.ws.send(JSON.stringify(payload));
  }

  private sendFrame(command: string, headers: Record<string, string>, body = ''): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const headerText = Object.entries(headers)
      .map(([k, v]) => `${k}:${v}`)
      .join('\n');
    const frame = `${command}\n${headerText}\n\n${body}\u0000`;
    this.debug('sending STOMP frame', {
      command,
      headers: Object.keys(headers),
      bodyPreview: this.preview(body),
    });
    this.ws.send(frame);
  }

  private handleRawPayload(rawText: string): void {
    let parsed: unknown = rawText;
    try {
      parsed = JSON.parse(rawText);
    } catch {
      // Keep raw text when payload is not JSON.
    }

    const payload = parsed as {
      type?: string;
      eventType?: string;
      destination?: string;
      topic?: string;
      convId?: string;
      conversationId?: string;
      data?: unknown;
      payload?: unknown;
      message?: unknown;
    };

    const normalizedType = String(payload?.type || payload?.eventType || '').toUpperCase();
    this.debug('parsed raw payload', {
      normalizedType,
      destination: String(payload?.destination || payload?.topic || '').trim(),
      convId: String(payload?.convId || payload?.conversationId || '').trim(),
      payloadPreview: this.preview(parsed),
    });
    if (normalizedType === 'CONNECTED') {
      this.clearConnectHandshakeTimer();
      this.mode = 'raw';
      this.connected = true;
      this.debug('received raw CONNECTED event');
      this.flushSubscriptions();
      return;
    }

    const destination = String(payload?.destination || payload?.topic || '').trim();
    if (destination) {
      const id = this.destinationToId.get(destination);
      if (id) {
        const sub = this.subscriptions.get(id);
        if (sub) {
          this.debug('dispatching raw payload by destination', { destination, id });
          sub.handler(payload?.data ?? payload?.payload ?? payload?.message ?? parsed);
          return;
        }
      }
    }

    // Route by conversation id if backend sends one payload stream for all topics.
    const convId = String(payload?.convId || payload?.conversationId || '').trim();
    if (convId) {
      for (const entry of this.subscriptions.values()) {
        if (entry.destination.endsWith(`/${convId}`)) {
          this.debug('dispatching raw payload by convId match', { convId, destination: entry.destination });
          entry.handler(payload?.data ?? payload?.payload ?? payload?.message ?? parsed);
          return;
        }
      }
    }

    // Last fallback: fan out to all subscribers.
    this.debug('dispatching raw payload as fan-out fallback', { subscribers: this.subscriptions.size });
    for (const entry of this.subscriptions.values()) {
      entry.handler(payload?.data ?? payload?.payload ?? payload?.message ?? parsed);
    }
  }

  private resolveDebugEnabled(): boolean {
    try {
      const flag = localStorage.getItem('omnifaichat.realtimeDebug')?.trim().toLowerCase();
      if (!flag) return true;
      return !['0', 'false', 'off', 'no'].includes(flag);
    } catch {
      return true;
    }
  }

  private debug(message: string, context?: Record<string, unknown>): void {
    if (!this.debugEnabled) return;
    if (context) {
      console.log(`[Realtime] ${message}`, context);
      return;
    }
    console.log(`[Realtime] ${message}`);
  }

  private redactToken(url: string): string {
    try {
      const u = new URL(url, window.location.origin);
      if (u.searchParams.has('token')) u.searchParams.set('token', '[redacted]');
      return u.toString();
    } catch {
      return url.replace(/token=[^&]+/i, 'token=[redacted]');
    }
  }

  private preview(value: unknown): string {
    const raw = typeof value === 'string' ? value : JSON.stringify(value);
    if (!raw) return '';
    return raw.length > 240 ? `${raw.slice(0, 240)}...` : raw;
  }
}
